import { isEmpty, trim } from 'lodash-es'

import { i18n } from 'locales'
import {
  locationConfigurationsSelector,
  ignoreLocationConfigurationsSelector
} from 'selectors/event_type'
import {
  selectedLocationConfigurationSelector
} from 'selectors'
import { isDialCodeOnly } from 'services/phone_number'

/** True if value is null, undefined, or a string that is empty/whitespace-only */
const isBlank = (value) => value == null || isEmpty(trim(value))

// i18n-tasks-use t('booking.templates.questions.errors.#{format}.blank')
const validatePresence = (value, format) => {
  if (!isBlank(value)) {
    return null
  }
  return i18n.t(`booking.templates.questions.errors.${format || 'string'}.blank`)
}

const validateFieldPresence = (field) => {
  if (!field.required) {
    return null
  }
  return validatePresence(field.value, field.format)
}

const validatePhoneNumber = (field) => {
  if (!field.value) {
    return null
  }
  if (!(!field.isValid || field.extension)) {
    return null
  }

  if (isDialCodeOnly(field.value, field.countryCode) && field.required === false) {
    return null
  }
  return i18n.t('booking.templates.questions.errors.phone_number.invalid')
}

const validates = {
  invitee(field) {
    switch (field.name) {
      case 'phone_number':
        return [validatePhoneNumber]
      default:
        return [validateFieldPresence]
    }
  },
  event_fields(field) {
    switch (field.format) {
      case 'phone_number':
        return [validateFieldPresence, validatePhoneNumber]
      default:
        return [validateFieldPresence]
    }
  }
}

const validateField = (model, field) => validates[model](field).reduce((memo, validate) => {
  const error = validate(field)
  if (error) {
    memo.push(error)
  }
  return memo
}, [])

const validateInvitee = (formState) => Object.values(formState.invitee).reduce((memo, field) => {
  const errors = validateField('invitee', field)
  if (errors.length) {
    memo[field.name] = errors
  }
  return memo
}, {})

const validateCustomFields = (formState) => formState.event_fields.reduce((memo, field, index) => {
  const errors = validateField('event_fields', field)
  if (errors.length) {
    memo[index] = {
      value: errors
    }
  }
  return memo
}, {})

const validateEvent = (formState, { state, isCancellation }) => {
  let errors
  const isLocationIgnored = ignoreLocationConfigurationsSelector(state)
  const isLocationPresent = !isEmpty(locationConfigurationsSelector(state))
  const isLocationExpected = !isCancellation && !isLocationIgnored && isLocationPresent
  const selectedLocation = selectedLocationConfigurationSelector(state)
  if (isLocationExpected && !selectedLocation.id) {
    return {
      location_choice: [validatePresence()]
    }
  }
  switch (selectedLocation.kind) {
    case 'ask_invitee':
      errors = [validatePresence(selectedLocation.location)].filter(Boolean)
      if (errors.length) {
        return {
          location: errors
        }
      }
      break
    case 'outbound_call':
      errors = [validatePresence(selectedLocation.phone_number), validatePhoneNumber(selectedLocation.phone_field)].filter(Boolean)
      if (errors.length) {
        return {
          phone_number: errors
        }
      }
      break
    default:
      return undefined
  }

  return undefined
}

const getValidators = () => [
  {
    model: 'invitee',
    validate: validateInvitee
  },
  {
    model: 'event',
    validate: validateEvent
  },
  {
    model: 'event_fields',
    validate: validateCustomFields
  }
]

export default (formState, options) => {
  const errors = getValidators().reduce((memo, validator) => {
    const result = validator.validate(formState, options)
    if (!isEmpty(result)) {
      memo[validator.model] = result
    }
    return memo
  }, {})
  return {
    valid: isEmpty(errors),
    errors
  }
}
